/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import Footer from "../../components/main/Footer"
import Basket from "../../components/organisms/Basket"
import { Header } from "../../components/organisms/Header"
import Optins from "../../components/organisms/Optins/Optins"
import { Topbar } from "../../components/organisms/Topbar"
import { STEPS, TENANTS } from "../../constants"
import { getCountryFromLanguage, initPdiComponent } from "../../core/service/BusinessLogicService"
import { getSessionVoyageurLang, resolveOptinsByLanguage } from "../../core/voyageurLanguage"
import { isItalianTenant, loc } from "../../core/utils"
import Optin from "../../form/Optin"
import { useLocalStorageServicePack } from "../../hooks"
import useGetOptinsByTenantAndCountry from "../../hooks/useGetOptinsByTenantAndCountry"
import { tunnelDataToFinalForm } from "../../mappers/tunnelDataMapper"
import { PackServiceContext } from "../ServicePage/context/PackServiceContext"
import { withUpdatedTunnelData } from "../hoc/withUpdatedTunnelData"
import AlgoanBankModal from "./components/AlgoanBankModal/AlgoanBankModal"
import BankCoordinatesBloc from "./components/BankCoordinatesBloc"
import BplsSummaryBlocOrder from "./components/BplsSummaryBlocOrder"
import BplsSummaryBottomFixedButtons from "./components/BplsSummaryBottomFixedButtons"
import BudgetBloc from "./components/BudgetBloc"
import CompanyBloc from "./components/CompanyBloc"
import CoordinatesBloc from "./components/CoordinatesBloc"
import IdentityBloc from "./components/IdentityBloc"
import IdentityDocumentsBloc from "./components/IdentityDocumentsBloc"
import MyAddressesBloc from "./components/MyAddressesBloc"
import SituationBloc from "./components/SituationBloc"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { Button, Col, Container, Form, Row } from "react-bootstrap"
import * as FinalForm from "react-final-form"
import { useToggle } from "react-use"

export default function SummaryPage({ tunnelData, onSetStep, history }) {
  const { packContext } = useContext(PackServiceContext)
  const { remove } = useLocalStorageServicePack()
  const { tenant } = tunnelData
  const customer = tunnelData.data.customer ?? tunnelData.data.order?.customer; // Si nous reprenons le dossier, nous ne passons pas par le mapper données clients -> tunnel Data
  const person = tunnelData.data.person ?? customer?.person;

  if(!tunnelData.data.customer) tunnelData.data.customer = customer;
  if(!tunnelData.data.person) tunnelData.data.person = person;

  const [isDisabled, toggleIsDisabled] = useToggle(false)
  const [algoanModalDismissed, setAlgoanModalDismissed] = useState(false)
  const formValuesRef = useRef({})

  const showAlgoanBankModal =
    tunnelData?.step === STEPS.ALGOAN_BACK &&
    tunnelData?.data?.order?.algoanJourneyEnabled === true &&
    !algoanModalDismissed

  const handleAlgoanContinue = (selectedBank) => {
    setAlgoanModalDismissed(true)
    const updatedData = {
      ...tunnelData.data,
      customer: {
        ...tunnelData.data.customer,
        bankAccountDTO: {
          ...tunnelData.data.customer.bankAccountDTO,
          iban: selectedBank.iban,
          bic: selectedBank.bic,
          accountKey: selectedBank.accountKey,
          batchId: selectedBank.batchId
        }
      }
    }
    onSubmit(formValuesRef.current, updatedData)
  }

  const enabled = tunnelData?.hideSummaryOptins === null || !tunnelData?.hideSummaryOptins
  const hasTunnelDataOptins = enabled && tunnelData?.optins?.length > 0
  const { optins: fetchedOptins, isLoadingOptins: isLoadingFetchedOptins } = useGetOptinsByTenantAndCountry(
    tunnelData,
    enabled && !hasTunnelDataOptins,
    STEPS.SUMMARY
  )
  const rawOptins = hasTunnelDataOptins ? tunnelData.optins : fetchedOptins
  const isLoadingOptins = hasTunnelDataOptins ? false : isLoadingFetchedOptins
  const selectedLanguage = getSessionVoyageurLang()
  const optins = useMemo(
    () => resolveOptinsByLanguage(rawOptins, selectedLanguage, tunnelData.optins),
    [rawOptins, selectedLanguage, tunnelData.optins]
  )

  const getOptinAnswers = (values) => {
    if (!optins || optins.length === 0) return []

    return optins?.map((optin) => {
      return {
        code: optin.code,
        checked: values[optin.code] === true || values[optin.code] === "true"
      }
    })
  }

  const onSubmit = (values, dataToMerge = null) => {
    remove()
    tunnelData.comeBackToClientStep = false
    const optinAnswers = getOptinAnswers(values)
    const payload = dataToMerge ? { ...dataToMerge, optins: optinAnswers } : { optins: optinAnswers }

    if (tenant === TENANTS.BPLS) {
      toggleIsDisabled(true)
      onSetStep(payload, false)
    } else {
      onSetStep(payload)
    }
  }

  const goBackToClientPage = () => {
    tunnelData.comeBackToClientStep = true
    onSetStep(tunnelData.data)
  }

  useEffect(() => {
    initPdiComponent("SummaryPage")
  }, [])

  if (!tunnelData.data.order) {
    tunnelData.data.order = { selectedServicePack: {}, rent: {} }
  }

  const configuration = tunnelData.clientPageConfiguration
  const country = getCountryFromLanguage(tunnelData.data.order.orderLanguageTag)

  const getRequired = (values) => {
    const hasRequiredOptins = optins?.some((optin) => optin.required === true)
    const requiredOptins = optins?.filter((optin) => optin.required === true)

    if (hasRequiredOptins) {
      return !requiredOptins?.every((optin) => values[optin.code] === true)
    } else {
      return false
    }
  }
  const UpdatedTunnelDataBasket = withUpdatedTunnelData(Basket, tunnelData, packContext)

  function handleInitialOptins(optins) {
    if (optins) {
      let optinValues = {}
      optins.forEach((optin) => {
        optinValues = {
          ...optinValues,
          [optin.code]: optin.checked
        }
      })
      return optinValues
    }
    return {}
  }

  return (
    <Container fluid className="white-background">
      <AlgoanBankModal show={showAlgoanBankModal} tunnelData={tunnelData} onContinue={handleAlgoanContinue} />
      <Row noGutters>
        <Col className="main-block">
          <Row>
            <Topbar tunnelData={tunnelData} history={history} />
            <Header tunnelData={tunnelData} />

            <Col sm={12}>
              <Container>
                {tenant === TENANTS.BPLS && <BplsSummaryBlocOrder tunnelData={tunnelData} />}
                {tenant !== TENANTS.BPLS && (
                  <Row>
                    <CompanyBloc
                      tunnelData={tunnelData}
                      values={tunnelDataToFinalForm(person, customer, country, tunnelData)}
                      configuration={configuration}
                    />
                    <IdentityBloc
                      tunnelData={tunnelData}
                      values={tunnelDataToFinalForm(person, customer, country, tunnelData)}
                      configuration={configuration}
                    />
                    <BankCoordinatesBloc
                      tunnelData={tunnelData}
                      values={tunnelDataToFinalForm(person, customer, country, tunnelData)}
                      configuration={configuration}
                    />
                    <MyAddressesBloc
                      tunnelData={tunnelData}
                      values={tunnelDataToFinalForm(person, customer, country, tunnelData)}
                      configuration={configuration}
                    />
                    <CoordinatesBloc
                      tunnelData={tunnelData}
                      values={tunnelDataToFinalForm(person, customer, country, tunnelData)}
                      configuration={configuration}
                    />
                    <IdentityDocumentsBloc
                      tunnelData={tunnelData}
                      values={tunnelDataToFinalForm(person, customer, country, tunnelData)}
                      configuration={configuration}
                    />
                    <SituationBloc
                      tunnelData={tunnelData}
                      values={tunnelDataToFinalForm(person, customer, country, tunnelData)}
                      configuration={configuration}
                    />
                    <BudgetBloc
                      tunnelData={tunnelData}
                      values={tunnelDataToFinalForm(person, customer, country, tunnelData)}
                      configuration={configuration}
                    />
                  </Row>
                )}
                <FinalForm.Form
                  initialValues={handleInitialOptins(tunnelData.data?.optins)}
                  onSubmit={onSubmit}
                  render={({ handleSubmit, values }) => {
                    formValuesRef.current = values
                    return (
                      <Form onSubmit={handleSubmit}>
                        {!tunnelData?.hideSummaryOptins ? (
                          <Row className="row-center mr-0 ml-0">
                            <Col className="radiofields optin-radio">
                              {tenant !== TENANTS.MORTAR &&
                                tenant !== TENANTS.BUT &&
                                tenant !== TENANTS.CONFORAMA_RETAIL &&
                                !isItalianTenant(tenant) &&
                                !isLoadingOptins && (
                                  <Optins
                                    optins={optins}
                                    tunnelData={tunnelData}
                                    showWeAskConsentTextBeforeOptins={country === "IT"}
                                  />
                                )}
                              {(tenant === TENANTS.MORTAR ||
                                tenant === TENANTS.BUT ||
                                tenant === TENANTS.CONFORAMA_RETAIL) &&
                                optins?.map((optIn) => (
                                  <Optin optin={optIn} key={optIn.code} tunnelData={tunnelData} />
                                ))}
                            </Col>
                          </Row>
                        ) : null}

                        <Row className="justify-content-between footer-fixed">
                          {tenant === TENANTS.BPLS && (
                            <BplsSummaryBottomFixedButtons
                              shopUrl={tunnelData?.shopUrl}
                              goBackToClientPage={goBackToClientPage}
                              isSubmitDisabled={getRequired(values) || isDisabled}
                              handleSubmit={handleSubmit}
                            />
                          )}
                          {tenant !== TENANTS.BPLS && (
                            <>
                              {tunnelData?.isFlowFromJourneyAtHome && (
                                <Row className="justify-content-center w-100">
                                  <Col lg={4} md={5} xs={12}>
                                    <Button
                                      id="continue"
                                      variant="success"
                                      type="submit"
                                      className="btn-solid btn"
                                      disabled={getRequired(values) || isDisabled}
                                    >
                                      {loc`Validate`}
                                    </Button>
                                  </Col>
                                </Row>
                              )}

                              {!tunnelData?.isFlowFromJourneyAtHome && (
                                <>
                                  <Col
                                    lg={{ span: 4, order: 1 }}
                                    md={{ span: 5, order: 1 }}
                                    xs={{ span: 12, order: 2 }}
                                  >
                                    <Button
                                      variant="outline-primary"
                                      onClick={goBackToClientPage}
                                      className="btn-outline-primary btn ico-edit-small-before"
                                    >
                                      {loc`Edit my data`}
                                    </Button>
                                  </Col>

                                  <Col
                                    lg={{ span: 3, order: 1 }}
                                    md={{ span: 5, order: 1 }}
                                    xs={{ span: 12, order: 1 }}
                                  >
                                    <Button
                                      id="continue"
                                      variant="success"
                                      type="submit"
                                      className="btn-solid btn"
                                      disabled={getRequired(values) || isDisabled}
                                    >
                                      {loc`Validate`}
                                    </Button>
                                  </Col>
                                </>
                              )}
                            </>
                          )}
                        </Row>
                      </Form>
                    )
                  }}
                />
              </Container>
            </Col>

            <Col sm={12}>
              <Container>
                <Footer tunnelData={tunnelData} />
              </Container>
            </Col>
          </Row>
        </Col>
        <Col className="aside-block d-none d-lg-block d-xl-block" lg={4}>
          <UpdatedTunnelDataBasket />
        </Col>
      </Row>
    </Container>
  )
}
